import { ref } from 'vue';
import ApiClient from '@/ApiClient';
import { endOfToday, isPast, startOfToday } from 'date-fns';
import { canFinalise } from '@/pages/talent-pool/utils/ShiftStatus';

export const globalShiftsState = {
  shifts: ref({}),
  loading: ref({}),
};

export async function fetchShifts(dataKey, locationId, status, dateFilter = {}) {
  globalShiftsState.loading.value[dataKey] = true;

  try {
    const response = await ApiClient.getTalentPoolShiftsByLocation(locationId, status, dateFilter);
    globalShiftsState.shifts.value[dataKey] = response.data;
  } catch (error) {
    console.error('Error fetching data:', error);
  } finally {
    globalShiftsState.loading.value[dataKey] = false;
  }
}

export const generateDataKey = (locationId, status, dateFilters) => {
  // Use args to create a hash
  return JSON.stringify({ locationId, status, dateFilters });
};

const statusesForTab = tab => {
  if (tab === 'open') {
    return ['UNFILLED', 'REJECTED', 'DENIED'];
  } else if (tab === 'pending') {
    return 'PENDING';
  } else if (tab === 'filled' || tab === 'finalise') {
    return 'APPROVED';
  } else if (tab === 'fulfilled') {
    return 'FULFILLED';
  } else if (tab === 'unfilled') {
    return ['UNFILLED', 'REJECTED', 'DENIED', 'PENDING'];
  }
  return undefined;
};

export const getTabForShift = shift => {
  const status = shift.most_relevant_claim_status;
  if (['UNFILLED', 'REJECTED', 'DENIED', 'PENDING'].includes(status) && isPast(shift.start_at)) {
    return 'unfilled';
  } else if (['UNFILLED', 'REJECTED', 'DENIED'].includes(status)) {
    return 'open';
  } else if (status === 'PENDING') {
    return 'pending';
  } else if (status === 'APPROVED') {
    return 'filled';
  } else if (status === 'FULFILLED') {
    return 'fulfilled';
  }
  return undefined;
};

const dateFilterForTab = tab => {
  if (tab === 'today') {
    // TODO: Double check this is correct with multi day shifts
    return { starts_before: endOfToday().toISOString(), ends_after: startOfToday().toISOString() };
  } else if (tab === 'unfilled') {
    return { starts_before: endOfToday().toISOString() };
  } else if (tab === 'finalise') {
    return {};
  } else if (tab === 'filled') {
    return { starts_after: startOfToday().toISOString() };
  } else if (tab === 'open' || tab === 'pending') {
    return { starts_after: startOfToday().toISOString() };
  }
  return {};
};

const applyTabFilter = (shifts, tab) => {
  if (tab === 'filled') {
    return shifts.filter(shift => !canFinalise(shift));
  } else if (tab === 'finalise') {
    return shifts.filter(shift => canFinalise(shift));
  } else if (tab === 'unfilled') {
    return shifts.filter(shift => isPast(shift.start_at));
  } else if (tab === 'open' || tab === 'pending') {
    return shifts.filter(shift => !isPast(shift.start_at));
  }
  return shifts;
};

export const getShiftsForTab = async (locationId, tab, forceFetch = false) => {
  const dateFilter = dateFilterForTab(tab);
  const statuses = statusesForTab(tab);
  const dataKey = generateDataKey(locationId, statuses, dateFilter);

  if (forceFetch || !globalShiftsState.shifts.value[dataKey]) {
    await fetchShifts(dataKey, locationId, statuses, dateFilter);
  }
  return applyTabFilter(globalShiftsState.shifts.value[dataKey], tab);
};
